import React from 'react';
import { Complaint, StatusUpdate } from '../interfaces/Complaint';

interface ComplaintDetailsModalProps {
  complaintDetails: Complaint | null;
  loading: boolean;
  onClose: () => void;
  onPrint: () => void;
  onUpdateStatus: (complaint: Complaint) => void;
}

const ComplaintDetailsModal: React.FC<ComplaintDetailsModalProps> = ({
  complaintDetails,
  loading,
  onClose,
  onPrint,
  onUpdateStatus,
}) => {
  const isLoading = loading || !complaintDetails;

  const getStatusStyle = (status: string) => {
    const styles = {
      'Pending': 'bg-amber-50 text-amber-700 border-amber-200',
      'Ongoing': 'bg-blue-50 text-blue-700 border-blue-200',
      'Resolved': 'bg-emerald-50 text-emerald-700 border-emerald-200',
      'Completed': 'bg-gray-50 text-gray-700 border-gray-200'
    };
    return styles[status as keyof typeof styles] || 'bg-gray-50 text-gray-700 border-gray-200';
  };

  console.log('Complaint Details:', complaintDetails);
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Enhanced Backdrop */}
      <div 
        className="absolute inset-0 bg-gray-900/60 backdrop-blur-sm transition-all duration-300"
        onClick={onClose}
      />
      
      {/* Professional Modal Container */}
      <div className="relative bg-white rounded-xl shadow-2xl max-w-5xl w-full max-h-[95vh] overflow-hidden z-10 border border-gray-100">
        {/* Sophisticated Header */}
        <div className="bg-gradient-to-r from-slate-50 to-gray-50 border-b border-gray-200/80 px-8 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="p-2 bg-blue-600 rounded-lg">
                <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
              <div>
                <h2 className="text-xl font-semibold text-gray-900 tracking-tight">Complaint Details</h2>
                <p className="text-sm text-gray-600 mt-0.5">Case Management System</p>
              </div>
            </div>
            
            <div className="flex items-center space-x-3">
              <button 
                className={`inline-flex items-center px-4 py-2.5 border border-gray-300 shadow-sm text-sm font-medium rounded-lg text-gray-700 bg-white hover:bg-gray-50 hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-all duration-200 ${
                  isLoading ? 'opacity-50 cursor-not-allowed' : ''
                }`}
                onClick={onPrint}
                disabled={isLoading}
              >
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
                </svg>
                Print Report
              </button>
              <button 
                className="text-gray-400 hover:text-gray-600 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 rounded-lg p-2 transition-all duration-200"
                onClick={onClose}
                aria-label="Close"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
        </div>
        
        {/* Enhanced Body */}
        <div className="overflow-y-auto max-h-[calc(95vh-200px)]">
          {isLoading ? (
            <div className="flex flex-col justify-center items-center py-24 px-8">
              {/* Professional Loading Animation */}
              <div className="relative mb-8">
                <div className="animate-spin rounded-full h-12 w-12 border-3 border-gray-200"></div>
                <div className="animate-spin rounded-full h-12 w-12 border-3 border-transparent border-t-blue-600 absolute top-0 left-0"></div>
              </div>
              
              <div className="text-center max-w-sm">
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  Loading Complaint Details
                </h3>
                <p className="text-sm text-gray-600 leading-relaxed">
                  Please wait while we retrieve the case information from our database.
                </p>
              </div>
              
              <div className="flex space-x-1 mt-6">
                {[0, 1, 2].map((i) => (
                  <div 
                    key={i}
                    className="animate-pulse bg-blue-600 rounded-full h-1.5 w-1.5" 
                    style={{animationDelay: `${i * 150}ms`}}
                  />
                ))}
              </div>
            </div>
          ) : (
            <div className="p-8">
              {/* Header Section */}
              <div className="mb-8 pb-6 border-b border-gray-100">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <h1 className="text-2xl font-semibold text-gray-900 mb-3 leading-tight">
                      {complaintDetails.subject}
                    </h1>
                    <div className="flex items-center space-x-4">
                      <span className={`inline-flex items-center px-3 py-1.5 rounded-full text-xs font-semibold border ${getStatusStyle(complaintDetails.status)}`}>
                        <span className={`w-1.5 h-1.5 rounded-full mr-2 ${
                          complaintDetails.status === 'Pending' ? 'bg-amber-400' :
                          complaintDetails.status === 'Ongoing' ? 'bg-blue-400' :
                          'bg-emerald-400'
                        }`} />
                        {complaintDetails.status}
                      </span>
                      <span className="text-sm text-gray-500">
                        Case #{complaintDetails.complain_id || 'N/A'}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Information Grid */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
                {/* Left Column */}
                <div className="space-y-6">
                  <div className="bg-gray-50 rounded-lg p-6">
                    <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
                      <svg className="w-5 h-5 text-gray-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                      </svg>
                      Complainant Information
                    </h3>
                    <div className="space-y-3">
                      <div>
                        <dt className="text-sm font-medium text-gray-600 mb-1">Full Name</dt>
                        <dd className="text-sm text-gray-900 font-medium">
                          {complaintDetails.first_name} {complaintDetails.last_name}
                        </dd>
                      </div>
                    </div>
                  </div>

                  <div className="bg-gray-50 rounded-lg p-6">
                    <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
                      <svg className="w-5 h-5 text-gray-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                      </svg>
                      Case Details
                    </h3>
                    <div className="space-y-3">
                      <div>
                        <dt className="text-sm font-medium text-gray-600 mb-1">Category</dt>
                        <dd className="text-sm text-gray-900">{complaintDetails.category || 'Uncategorized'}</dd>
                      </div>
                      <div>
                        <dt className="text-sm font-medium text-gray-600 mb-1">Date Submitted</dt>
                        <dd className="text-sm text-gray-900">
                          {new Date(complaintDetails.created_at).toLocaleDateString('en-US', {
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </dd>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Right Column */}
                <div className="space-y-6">
                  <div className="bg-gray-50 rounded-lg p-6">
                    <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
                      <svg className="w-5 h-5 text-gray-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      Description
                    </h3>
                    <p className="text-sm text-gray-700 leading-relaxed whitespace-pre-wrap">
                      {complaintDetails.description}
                    </p>
                  </div>

                  {/* Attached Photo */}
                  {complaintDetails.photo_url && (
                    <div className="bg-gray-50 rounded-lg p-6">
                      <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
                        <svg className="w-5 h-5 text-gray-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                        Attached Evidence
                      </h3>
                      <a 
                        href={`${process.env.REACT_APP_BASE_URL || 'http://10.50.227.120:3000'}${complaintDetails.photo_url}`} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="block hover:opacity-95 transition-opacity"
                      >
                        <img 
                          src={`${process.env.REACT_APP_BASE_URL || 'http://10.50.227.120:3000'}${complaintDetails.photo_url}`} 
                          alt="Complaint Evidence" 
                          className="w-full h-auto rounded-lg shadow-sm border border-gray-200 hover:shadow-md transition-shadow" 
                        />
                      </a>
                    </div>
                  )}
                </div>
              </div>
              
              {/* Status Updates Section */}
              <div className="border-t border-gray-100 pt-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-6 flex items-center">
                  <svg className="w-6 h-6 text-gray-600 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                  </svg>
                  Status History
                </h3>
                
                {complaintDetails.status_updates && complaintDetails.status_updates.length > 0 ? (
                  <div className="space-y-4">
                    {complaintDetails.status_updates.map((update: StatusUpdate, index: number) => (
                      <div key={update.update_id || index} className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm hover:shadow-md transition-shadow">
                        <div className="flex justify-between items-start mb-4">
                          <div className="flex items-center justify-center space-x-3">
                            <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                              <span className="text-xs font-medium text-blue-700">
                                {update.first_name?.[0]}{update.last_name?.[0]}
                              </span>
                            </div>
                            <div className='flex items-center justify-center'>
                              <p className="text-sm font-medium text-gray-900">
                                {update.first_name} {update.last_name}
                              </p>
                            </div>
                          </div>
                          <div className="text-right">
                            <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium border ${getStatusStyle(update.updated_status)}`}>
                              {update.updated_status}
                            </span>
                            <p className="text-xs text-gray-500 mt-1">
                              {update.created_at ? new Date(update.created_at).toLocaleDateString('en-US', {
                                month: 'short',
                                day: 'numeric',
                                year: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit'
                              }) : 'N/A'
                              }
                            </p>
                          </div>
                        </div>
                        <div className="pl-11">
                          <p className="text-sm text-gray-700 leading-relaxed">{update.update_text}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12 bg-gray-50 rounded-lg border-2 border-dashed border-gray-200">
                    <svg className="w-12 h-12 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                    </svg>
                    <p className="text-sm font-medium text-gray-900 mb-1">No status updates available</p>
                    <p className="text-xs text-gray-500">Updates will appear here as the case progresses</p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
        
        {/* Professional Footer */}
        {!isLoading && (
          <div className="bg-gray-50 border-t border-gray-200 px-8 py-6">
            <div className="flex justify-end">
              <button 
                className="inline-flex items-center px-6 py-3 border border-transparent text-sm font-medium rounded-lg shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-all duration-200"
                onClick={() => onUpdateStatus(complaintDetails)}
              >
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                </svg>
                Update Case Status
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ComplaintDetailsModal;